import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import AccessibleButton from '../components/common/AccessibleButton';
import { stripInternalFields } from '../utils/privacy';
import { displayNameFromDashboard } from '../utils/studentDashboard';
import { CompassIcon } from '../features/careerDirection/icons';

/** Short "20 min | 3 hrs | 5 question quiz" line for a schedule entry. */
function moduleDetailLine(entry) {
    if (!entry) return '';
    return [
        entry.durationMinutes ? `${entry.durationMinutes} min` : null,
        entry.requiredHours ? `${entry.requiredHours} hrs` : null,
        entry.hasQuiz ? `${entry.questionCount} question quiz` : null
    ].filter(Boolean).join(' \u00b7 ');
}

const Dashboard = () => {
    const { user } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await axios.get('/student/dashboard');
            setDashboard(stripInternalFields(response.data.data || {}));
        } catch (err) {
            setDashboard(null);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    if (loading) {
        return (
            <div className="space-y-6" role="status" aria-live="polite">
                <div className="skeleton h-10 w-64" />
                <div className="skeleton h-40 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="skeleton h-28" />
                    <div className="skeleton h-28" />
                    <div className="skeleton h-28" />
                </div>
                <div className="skeleton h-64 w-full" />
            </div>
        );
    }

    if (error || !dashboard) {
        return (
            <div className="card space-y-4" role="alert">
                <h1 className="text-2xl font-bold font-serif text-ink-950">Unable to load your progress right now</h1>
                <p className="text-sm text-ink-600">Please try again in a moment.</p>
                <AccessibleButton onClick={loadDashboard} ariaLabel="Retry loading dashboard">
                    Retry
                </AccessibleButton>
            </div>
        );
    }

    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) return 'Good morning';
        if (hour >= 12 && hour < 17) return 'Good afternoon';
        if (hour >= 17 && hour < 21) return 'Good evening';
        return 'Good night';
    };

    const firstName = displayNameFromDashboard(dashboard, user);
    const greeting = getTimeBasedGreeting();
    const careerDirection = dashboard.careerDirection || null;
    const learning = dashboard.learning || {};
    const profileComp = dashboard.profile || {};
    const schedule = Array.isArray(learning.schedule) ? learning.schedule : [];
    const currentItem = learning.currentItem || learning.nextItem || null;

    // Profile completion — the nine required fields, counted by the API.
    const requiredTotal = Number(profileComp.totalCount) || 9;
    const completedFields = Number(profileComp.completedCount) || 0;
    const isProfileComplete = profileComp.isComplete === true;
    const missingFields = Array.isArray(profileComp.missingFields) ? profileComp.missingFields : [];

    // Active module state, from this intern's real submission.
    const isQuizReady = Boolean(currentItem?.quizReady);
    const isModuleInProgress = currentItem?.status === 'in_progress';
    const moduleHref = currentItem
        ? (currentItem.videoModuleId
            ? `/learning-modules/${currentItem.videoModuleId}`
            : `/learning/item/${currentItem.itemId || currentItem.id}`)
        : '/learning';

    const totalModules = Number(learning.total) || 0;
    const completedModules = Number(learning.completedCount ?? learning.completed) || 0;
    // A curriculum step can carry several videos, so these two differ.
    const videoCount = Number(learning.videoModuleCount) || 0;

    // Subtitle copy — describes only what we actually know.
    let subtext = 'Complete your Talent Profile to open your first module.';
    if (!isProfileComplete) {
        const remaining = Math.max(0, requiredTotal - completedFields);
        subtext = remaining === 1
            ? 'One field left before your first module opens.'
            : `${remaining} fields left before your first module opens.`;
    } else if (isQuizReady) {
        subtext = `${currentItem?.title} content is done. The quiz is the last step.`;
    } else if (isModuleInProgress) {
        subtext = `${currentItem?.title} is open and waiting for you.`;
    } else if (currentItem) {
        subtext = `${currentItem.title} is ready to start.`;
    } else if (totalModules > 0 && completedModules === totalModules) {
        subtext = 'You have finished every module assigned to you. Great work.';
    } else {
        subtext = 'Your profile is complete. Your first module will appear here once it is assigned.';
    }

    return (
        <div className="space-y-6 overflow-x-hidden pb-12">
            {/* Header Greeting */}
            <div>
                <h1 className="text-3xl md:text-4xl font-serif font-bold text-ink-950 tracking-tight">
                    {greeting}, {firstName}
                </h1>
                {careerDirection && careerDirection.job_title && (
                    <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-sm text-brand-800">
                        <CompassIcon className="h-4 w-4 shrink-0" />
                        <span className="text-xs font-medium uppercase tracking-wider text-brand-700">Career direction</span>
                        <span className="font-semibold text-ink-950">{careerDirection.job_title}</span>
                    </p>
                )}
                {careerDirection && !careerDirection.job_title && (
                    <p className="mt-2 text-xs text-ink-500">
                        <Link to="/profile" className="font-medium text-brand-700 underline underline-offset-2">Add your degree</Link> to see your career direction.
                    </p>
                )}
                <p className="mt-1 text-sm text-ink-600 font-medium">{subtext}</p>
            </div>

            {/* YOUR NEXT STEP - Hero Card */}
            <div className="bg-white rounded-2xl border border-border p-6 shadow-sm space-y-4 animate-fade-up">
                <div className="text-[11px] font-bold uppercase tracking-wider text-brand-700">
                    YOUR NEXT STEP
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2 max-w-2xl">
                        <h2 className="text-2xl font-serif font-bold text-ink-950">
                            {!isProfileComplete
                                ? 'Complete your Talent Profile'
                                : isQuizReady
                                    ? `Take the ${currentItem.title} quiz`
                                    : currentItem
                                        ? `${isModuleInProgress ? 'Continue' : 'Start'} ${currentItem.title}`
                                        : 'Nothing is waiting on you'}
                        </h2>

                        <p className="text-xs text-ink-600 leading-relaxed">
                            {!isProfileComplete
                                ? `${completedFields} of ${requiredTotal} required fields done. Your first module opens the moment your profile is complete.`
                                : isQuizReady
                                    ? 'Passing the quiz completes this module and unlocks what comes next.'
                                    : currentItem
                                        ? (moduleDetailLine(currentItem) || 'Open the module to get started.')
                                        : 'Your profile is complete and you are up to date on every assigned module.'}
                        </p>

                        {/* Progress / missing-field pills */}
                        {!isProfileComplete ? (
                            <div className="space-y-2 pt-1">
                                <div className="flex items-center gap-3 text-xs font-semibold text-ink-700">
                                    <div className="flex-1 h-2 bg-ink-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-brand-700 rounded-full transition-all"
                                            style={{ width: `${Math.round((completedFields / requiredTotal) * 100)}%` }}
                                        />
                                    </div>
                                    <span className="font-mono text-xs">{completedFields} / {requiredTotal}</span>
                                </div>
                                {missingFields.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                        {missingFields.map((m) => (
                                            <span
                                                key={m.key || m.label}
                                                className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-900 border border-amber-200 text-[11px] font-medium"
                                            >
                                                Missing: {m.label}{m.tab ? ` · ${m.tab}` : ''}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : isQuizReady ? (
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                {currentItem.questionCount > 0 && (
                                    <span className="px-2.5 py-1 bg-surface border border-border rounded-md text-[11px] font-medium text-ink-700">
                                        {currentItem.questionCount} question{currentItem.questionCount === 1 ? '' : 's'}
                                    </span>
                                )}
                                {currentItem.passingPercentage != null && (
                                    <span className="px-2.5 py-1 bg-surface border border-border rounded-md text-[11px] font-medium text-ink-700">
                                        Pass mark {currentItem.passingPercentage}%
                                    </span>
                                )}
                                <span className="px-2.5 py-1 bg-surface border border-border rounded-md text-[11px] font-medium text-ink-700">
                                    {currentItem.attemptsRemaining} attempt{currentItem.attemptsRemaining === 1 ? '' : 's'} left
                                </span>
                            </div>
                        ) : currentItem ? (
                            <div className="space-y-1.5 pt-1">
                                <div className="flex items-center gap-3 text-xs font-semibold text-ink-700">
                                    <div className="flex-1 h-2 bg-ink-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-brand-700 rounded-full transition-all"
                                            style={{ width: `${currentItem.progress}%` }}
                                        />
                                    </div>
                                    <span className="text-[11px] text-ink-600 font-medium">{currentItem.progress}% complete</span>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
                        {!isProfileComplete ? (
                            <Link to="/profile" className="btn-primary px-6 py-2.5 bg-brand-700 text-white hover:bg-brand-800 font-semibold text-xs rounded-xl shadow-sm text-center">
                                Finish profile
                            </Link>
                        ) : currentItem ? (
                            <Link to={moduleHref} className="btn-primary px-6 py-2.5 bg-brand-700 text-white hover:bg-brand-800 font-semibold text-xs rounded-xl shadow-sm text-center">
                                {isQuizReady ? 'Start quiz' : isModuleInProgress ? 'Continue module' : 'Start module'}
                            </Link>
                        ) : null}
                        <Link to="/learning" className="px-4 py-2 bg-white text-ink-700 border border-border hover:bg-surface font-semibold text-xs rounded-xl text-center transition-colors">
                            See full schedule
                        </Link>
                    </div>
                </div>
            </div>

            {/* Metric stat cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-border p-5 shadow-sm space-y-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">PROFILE</div>
                    <div className="text-2xl font-bold text-ink-950">
                        {isProfileComplete ? 'Complete' : `${completedFields} of ${requiredTotal}`}
                    </div>
                    <div className="text-xs text-ink-500 font-medium">
                        {isProfileComplete
                            ? `All ${requiredTotal} fields done`
                            : `${requiredTotal - completedFields} field${requiredTotal - completedFields === 1 ? '' : 's'} remaining`}
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-border p-5 shadow-sm space-y-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">CURRENT MODULE</div>
                    <div className="text-2xl font-bold text-ink-950">
                        {!isProfileComplete
                            ? 'Not started'
                            : isQuizReady
                                ? 'Awaiting quiz'
                                : isModuleInProgress
                                    ? 'In progress'
                                    : currentItem
                                        ? 'Ready to start'
                                        : 'None active'}
                    </div>
                    <div className="text-xs text-ink-500 font-medium">
                        {!isProfileComplete
                            ? 'Unlocks with your profile'
                            : currentItem
                                ? currentItem.title
                                : 'Nothing assigned right now'}
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-border p-5 shadow-sm space-y-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">LEARNING PROGRESS</div>
                    <div className="text-2xl font-bold text-ink-950">
                        {totalModules > 0 ? `${completedModules} of ${totalModules}` : '—'}
                    </div>
                    <div className="text-xs text-ink-500 font-medium">
                        {totalModules > 0
                            ? (videoCount > totalModules
                                ? `steps complete · ${videoCount} videos`
                                : 'modules complete')
                            : 'no modules assigned yet'}
                    </div>
                </div>
            </div>

            {/* Your schedule */}
            <div className="bg-white rounded-2xl border border-border p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-serif font-bold text-ink-950">Your schedule</h3>
                    <Link to="/learning" className="text-xs font-semibold text-brand-700 hover:underline">
                        Open Learning &amp; Training <ArrowRightIcon className="inline h-3 w-3" aria-hidden="true" />
                    </Link>
                </div>

                {schedule.length === 0 ? (
                    <p className="text-sm text-ink-600">
                        {isProfileComplete
                            ? 'No modules have been assigned to you yet. Your mentor will add them here.'
                            : 'Your schedule appears here once your Talent Profile is complete.'}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {schedule.map((entry) => {
                            const label = entry.isCompleted
                                ? 'Complete'
                                : entry.isLocked
                                    ? 'Locked'
                                    : entry.quizReady
                                        ? 'Quiz ready'
                                        : entry.status === 'in_progress'
                                            ? 'In progress'
                                            : 'Available';
                            const badgeClass = entry.isCompleted
                                ? 'bg-emerald-100 text-emerald-800'
                                : entry.isLocked
                                    ? 'bg-ink-100 text-ink-600'
                                    : entry.quizReady
                                        ? 'bg-amber-100 text-amber-900'
                                        : 'bg-brand-100 text-brand-800';
                            const rowClass = entry.isLocked
                                ? 'bg-surface border-border opacity-80'
                                : entry.isCompleted
                                    ? 'bg-emerald-50/40 border-emerald-200'
                                    : 'bg-brand-50/40 border-brand-200';
                            const href = entry.videoModuleId
                                ? `/learning-modules/${entry.videoModuleId}`
                                : `/learning/item/${entry.itemId || entry.id}`;

                            const row = (
                                <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${rowClass}`}>
                                    <div className="flex items-start gap-3">
                                        <span className="text-base mt-0.5" aria-hidden="true">
                                            {entry.isCompleted ? '✅' : entry.isLocked ? '🔒' : '📘'}
                                        </span>
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h4 className="font-bold text-sm text-ink-950">{entry.title}</h4>
                                                {entry.moduleCount > 1 && (
                                                    <span className="px-2 py-0.5 rounded-md bg-ink-100 text-ink-600 text-[10px] font-bold uppercase tracking-wide">
                                                        Video {entry.modulePosition} of {entry.moduleCount}
                                                    </span>
                                                )}
                                            </div>
                                            {entry.moduleCount > 1 && entry.pathItemTitle !== entry.title && (
                                                <p className="text-[11px] text-ink-500 mt-0.5">{entry.pathItemTitle}</p>
                                            )}
                                            <p className="text-xs text-ink-600 mt-0.5">
                                                {entry.isLocked
                                                    ? 'Unlocks when the previous step is complete'
                                                    : (moduleDetailLine(entry) || 'Open to get started')}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-center self-start sm:self-auto ${badgeClass}`}>
                                        {label}
                                    </span>
                                </div>
                            );

                            // entry.id is per row; assignmentId repeats when a
                            // step carries more than one video.
                            return entry.isLocked ? (
                                <div key={entry.id}>{row}</div>
                            ) : (
                                <Link key={entry.id} to={href} className="block hover:opacity-90 transition-opacity">
                                    {row}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
